import { useEffect, useState, useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contexts/auth';
import { DebitosContext } from '../../contexts/debitos';
import { useLoading } from '../../contexts/LoadingContext';
import { AppLayout } from '../../components/AppLayout';
import { CardIcon } from '../../components/CardIcon';
import { FluidChartContainer } from '../../components/charts/FluidChartContainer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { intlLocale } from '../../Utils/locale';
import { useCurrency } from '../../contexts/Currency';
import {
  LayoutDashboard,
  Wallet,
  TrendingUp,
  DollarSign,
  Target,
  Percent,
} from 'lucide-react';
import { getUSDQuote } from '../../api/currency';
import { getDashboardData } from '../../api/dashboard';
import { listSimulations } from '../../api/simulations';
import { KpiCard } from '../../components/charts/KpiCard';
import { RevenueExpensesChart } from '../../components/charts/RevenueExpensesChart';
import { CategoryDonutChart } from '../../components/charts/CategoryDonutChart';
import { EvolutionAreaChart } from '../../components/charts/EvolutionAreaChart';
import { CHART_COLORS, EASING } from '../../components/charts/chart-tokens';

function Dashboard() {
  const { user } = useContext(AuthContext);
  const { debitos, getDebitos } = useContext(DebitosContext);
  const { t, i18n } = useTranslation();
  const { withLoading } = useLoading();

  const [usdQuote, setUsdQuote] = useState(null);
  const [usdQuoteError, setUsdQuoteError] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [nextGoal, setNextGoal] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadAllData(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAllData = async (userId) => {
    await withLoading(async () => {
      await Promise.all([
        getDebitos(userId),
        loadUSDQuote(),
        loadDashboardData(userId),
        loadNextGoal(userId)
      ]);
    }, t('loadingDashboardData'));
  };

  const loadUSDQuote = async () => {
    try {
      setUsdQuoteError(false);
      const quote = await getUSDQuote();
      setUsdQuote(quote);
    } catch (error) {
      console.error('Erro ao carregar cotação do dólar:', error);
      setUsdQuoteError(true);
    }
  };

  const loadNextGoal = async (userId) => {
    try {
      const simulations = await listSimulations(userId);
      const incomplete = (simulations || [])
        .filter(s => s.percentualAtingido == null || s.percentualAtingido < 100)
        .sort((a, b) => (b.percentualAtingido || 0) - (a.percentualAtingido || 0));
      setNextGoal(incomplete[0] || null);
    } catch (error) {
      console.error('Erro ao carregar objetivos:', error);
    }
  };

  const loadDashboardData = async (userId) => {
    try {
      const data = await getDashboardData(userId);
      setDashboardData(data);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    }
  };

  const metrics = useMemo(() => {
    if (!dashboardData) {
      return {
        rendaMensal: 0,
        gastosDoMes: 0,
        totalInvestimentos: 0,
        rendaPassiva: 0
      };
    }

    return {
      rendaMensal: dashboardData.current.receita,
      gastosDoMes: dashboardData.current.gastosDoMes,
      totalInvestimentos: dashboardData.current.totalInvestimentos,
      rendaPassiva: dashboardData.current.rendaPassiva
    };
  }, [dashboardData]);

  const categoryData = useMemo(() => {
    const categorias = {};
    const now = new Date();
    const currentMesRef = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    debitos.forEach(debito => {
      // Pie chart só do mês atual (evita somar débitos de meses passados/futuros).
      if (debito?.dataReferencia && debito.dataReferencia !== currentMesRef) return;

      const categoria = debito.categoria || t('others');
      const valor = parseFloat(debito.valor || 0);

      if (categorias[categoria]) {
        categorias[categoria] += valor;
      } else {
        categorias[categoria] = valor;
      }
    });

    return Object.entries(categorias).map(([nome, valor]) => ({
      nome,
      valor: parseFloat(valor.toFixed(2))
    }));
  }, [debitos, t]);

  const monthlyData = useMemo(() => {
    if (!dashboardData || !dashboardData.monthly) {
      return [];
    }

    return dashboardData.monthly.map(item => {
      const date = new Date(item.year, item.month - 1, 1);
      const monthName = date.toLocaleDateString(intlLocale(i18n.language), { month: 'short' });

      return {
        mes: monthName,
        receita: item.receita,
        despesas: item.despesas,
        investido: item.investimentos
      };
    });
  }, [dashboardData, i18n.language]);

  const investmentGrowthData = useMemo(() => {
    return monthlyData;
  }, [monthlyData]);

  const taxaPoupanca = useMemo(() => {
    if (metrics.rendaMensal === 0) return 0;
    const economizado = metrics.rendaMensal - metrics.gastosDoMes;
    return ((economizado / metrics.rendaMensal) * 100).toFixed(1);
  }, [metrics]);

  const { format: formatCurrency } = useCurrency();

  const formatCompactAxisValue = (value) => {
    return new Intl.NumberFormat(intlLocale(i18n.language), {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  const getTickFontSize = (width) => {
    if (width <= 320) return 9;
    if (width <= 375) return 10;
    if (width <= 540) return 11;
    return 12;
  };

  const getXAxisInterval = (width) => {
    if (width <= 320) return 2;
    if (width <= 412) return 1;
    return 0;
  };

  const getChartMargin = (width) => ({
    top: 8,
    right: width <= 375 ? 8 : 16,
    left: width <= 375 ? -18 : width <= 540 ? -10 : 0,
    bottom: width <= 375 ? 8 : 4,
  });


  const changes = useMemo(() => {
    if (!dashboardData || !dashboardData.monthly || dashboardData.monthly.length < 2) {
      return { expenseChange: 0, investmentChange: 0, dividendChange: 0 };
    }

    const monthly = dashboardData.monthly;
    const current = monthly[monthly.length - 1];
    const previous = monthly[monthly.length - 2];

    const expenseChange = previous.despesas > 0
      ? ((current.despesas - previous.despesas) / previous.despesas) * 100
      : 0;

    const investmentChange = previous.investimentos > 0
      ? ((current.investimentos - previous.investimentos) / previous.investimentos) * 100
      : 0;

    return { expenseChange, investmentChange, dividendChange: 0 };
  }, [dashboardData]);

  const isOverspending = metrics.gastosDoMes > metrics.rendaMensal;

  const sparklines = useMemo(() => {
    const recent = (dashboardData?.monthly || []).slice(-6);
    const toSeries = (key) => recent.map((m) => ({ value: Number(m[key]) || 0 }));
    return {
      receita: toSeries('receita'),
      despesas: toSeries('despesas'),
      investimentos: toSeries('investimentos'),
      rendaPassiva: toSeries('rendaPassiva'),
    };
  }, [dashboardData]);

  const summaryCards = [
    {
      title: t('monthlyIncome'),
      value: formatCurrency(metrics.rendaMensal),
      icon: DollarSign,
      gradient: 'from-blue-500 to-indigo-600',
      sparkline: sparklines.receita,
      sparklineColor: CHART_COLORS.income.base,
      trend: null,
      subtitle: t('monthlyFixed'),
    },
    {
      title: t('monthlyExpenses'),
      value: formatCurrency(metrics.gastosDoMes),
      icon: Wallet,
      gradient: isOverspending
        ? 'from-rose-500 to-red-600'
        : 'from-violet-500 to-fuchsia-600',
      sparkline: sparklines.despesas,
      sparklineColor: CHART_COLORS.expense.base,
      trend: changes.expenseChange,
      trendUpIsBad: true,
      warning: isOverspending,
      warningLabel: t('aboveIncome'),
    },
    {
      title: t('invested'),
      value: formatCurrency(metrics.totalInvestimentos),
      icon: TrendingUp,
      gradient: 'from-pink-500 to-rose-500',
      sparkline: sparklines.investimentos,
      sparklineColor: CHART_COLORS.invested.base,
      trend: changes.investmentChange,
    },
    {
      title: t('passiveIncome'),
      value: formatCurrency(metrics.rendaPassiva),
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-teal-600',
      sparkline: sparklines.rendaPassiva,
      sparklineColor: CHART_COLORS.passive.base,
      trend: changes.dividendChange,
    },
  ];

  return (
    <AppLayout
      title={t('dashboard')}
      subtitle={t('dashboardSubtitle')}
      icon={LayoutDashboard}
    >
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 min-[1200px]:grid-cols-4">
            {summaryCards.map((card, index) => (
              <KpiCard
                key={index}
                index={index}
                icon={card.icon}
                iconGradient={card.gradient}
                title={card.title}
                value={card.value}
                trendPercent={card.trend}
                trendUpIsBad={card.trendUpIsBad}
                warning={card.warning}
                warningLabel={card.warningLabel}
                subtitle={card.subtitle}
                sparklineData={card.sparkline}
                sparklineColor={card.sparklineColor}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: EASING.smoothOut }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          >
            <Card className="min-w-0 border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
                <CardTitle>{t('monthlyOverviewIncomeExpenses')}</CardTitle>
                <p className="text-sm text-slate-500 mt-1">{t('last12Months')}</p>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <FluidChartContainer height="clamp(16rem, 52vw, 22rem)">
                  {(chartWidth) => (
                    <RevenueExpensesChart
                      data={monthlyData}
                      margin={getChartMargin(chartWidth)}
                      xAxisInterval={getXAxisInterval(chartWidth)}
                      minTickGap={chartWidth <= 412 ? 10 : 18}
                      tickFontSize={getTickFontSize(chartWidth)}
                      yAxisWidth={chartWidth <= 375 ? 42 : chartWidth <= 540 ? 52 : 60}
                      tickFormatter={formatCompactAxisValue}
                      formatCurrency={formatCurrency}
                      incomeLegend={t('incomeLegend')}
                      expensesLegend={t('expensesLegend')}
                    />
                  )}
                </FluidChartContainer>
              </CardContent>
            </Card>

            <Card className="min-w-0 border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
                <CardTitle>{t('expensesByCategory')}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <CategoryDonutChart
                  data={categoryData}
                  formatCurrency={formatCurrency}
                  centerLabel={t('total') || 'Total'}
                />
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5, ease: EASING.smoothOut }}
          >
            <Card className="mb-8 min-w-0 border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
                <CardTitle>{t('investmentExpensesEvolution')}</CardTitle>
                <p className="text-sm text-slate-500 mt-1">{t('last12Months')}</p>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <FluidChartContainer height="clamp(16rem, 50vw, 22rem)">
                  {(chartWidth) => (
                    <EvolutionAreaChart
                      data={investmentGrowthData}
                      margin={getChartMargin(chartWidth)}
                      xAxisInterval={getXAxisInterval(chartWidth)}
                      minTickGap={chartWidth <= 412 ? 10 : 18}
                      tickFontSize={getTickFontSize(chartWidth)}
                      yAxisWidth={chartWidth <= 375 ? 42 : chartWidth <= 540 ? 52 : 60}
                      tickFormatter={formatCompactAxisValue}
                      formatCurrency={formatCurrency}
                      investmentsLegend={t('investmentsLegend')}
                      expensesLegend={t('expensesLegend')}
                    />
                  )}
                </FluidChartContainer>
              </CardContent>
            </Card>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 min-[1200px]:grid-cols-4">
            <Card className="min-w-0 border-l-4 border-l-yellow-500">
              <CardContent className="p-6">
                <div className="flex min-w-0 items-start gap-4">
                  <CardIcon icon={DollarSign} className="bg-gradient-to-br from-yellow-500 to-amber-600" iconClassName="text-white" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-500 mb-1">USD-BRL</p>
                    {usdQuote ? (
                      <>
                        <p className="text-lg font-semibold text-slate-900">
                          R$ {parseFloat(usdQuote.bid).toFixed(2)}
                        </p>
                        <p className={`text-xs ${usdQuote.pctChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {usdQuote.pctChange > 0 ? '+' : ''}{usdQuote.pctChange.toFixed(2)}%
                        </p>
                      </>
                    ) : usdQuoteError ? (
                      <p className="text-sm text-red-500">{t('unavailable')}</p>
                    ) : (
                      <p className="text-sm text-slate-400">{t('loadingShort')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardContent className="p-6">
                <div className="flex min-w-0 items-start gap-4">
                  <CardIcon icon={Target} className="bg-gradient-to-br from-blue-500 to-indigo-600" iconClassName="text-white" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-500 mb-1">{t('nextGoalLabel')}</p>
                    {nextGoal ? (
                      <>
                        <p className="text-lg font-semibold text-slate-900 truncate" title={nextGoal.objetivo}>
                          {nextGoal.objetivo}
                        </p>
                        <p className="text-xs text-slate-500">
                          {nextGoal.percentualAtingido != null
                            ? `${nextGoal.percentualAtingido.toFixed(1)}% ${t('completed')}`
                            : t('inProgress')}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-400">{t('noGoalsRegistered')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardContent className="p-6">
                <div className="flex min-w-0 items-start gap-4">
                  <CardIcon icon={Percent} className="bg-gradient-to-br from-green-500 to-emerald-600" iconClassName="text-white" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-500 mb-1">{t('savingsRate')}</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {taxaPoupanca}% {t('ofIncome')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardContent className="p-6">
                <div className="flex min-w-0 items-start gap-4">
                  <CardIcon icon={TrendingUp} className="bg-gradient-to-br from-purple-500 to-pink-600" iconClassName="text-white" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-500 mb-1">{t('recordedHistory')}</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {dashboardData?.monthly?.length || 0} {t('monthsUnit')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
    </AppLayout>
  );
}

export default Dashboard;
